<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">ANNA Settings</oxd-text>
      <oxd-divider />
      <oxd-text tag="p" class="anna-intro">
        What Anna asks and how she works. Change what you need, then "Save
        changes" on the tab; the very next call or email uses it.
      </oxd-text>
      <!-- whether candidates get real emails: the switch that matters most, always in sight -->
      <div
        v-if="loaded"
        :class="['anna-mode', testMode ? 'anna-mode--test' : 'anna-mode--live']"
      >
        <span class="anna-mode-badge">{{ testMode ? 'TEST' : 'LIVE' }}</span>
        <span>
          {{
            testMode
              ? `Test mode: every candidate email goes to ${
                  testAddress || '(no test address yet)'
                }, not to the candidate.`
              : 'Candidate emails go to the real candidates.'
          }}
        </span>
        <oxd-button
          display-type="text"
          label="Change"
          @click="goToCard('outreach-hr')"
        />
      </div>
      <oxd-text v-if="offline" tag="p" class="anna-warn">
        {{ offline }} These are ANNA's settings as last seen; changes can be
        saved again once she's back.
      </oxd-text>
      <oxd-text
        v-else-if="loaded && !encryptionEnabled"
        tag="p"
        class="anna-warn"
      >
        ANNA's server has no ANNA_SECRET_KEY, so secret keys cannot be saved.
      </oxd-text>
      <div class="anna-tabs" role="tablist">
        <button
          v-for="a in areas"
          :key="a"
          type="button"
          role="tab"
          :aria-selected="area === a"
          :class="['anna-tab', {'anna-tab--active': area === a}]"
          @click="area = a"
        >
          {{ a }}
          <span
            v-if="a === ADMIN && adminNeedsSetup"
            class="anna-tab-dot"
            title="Something here needs setting up"
          ></span>
        </button>
      </div>
    </div>

    <!-- every area stays rendered (v-show), so unsaved edits survive switching -->
    <div v-for="a in areas" v-show="area === a" :key="a">
      <!-- one Save for the whole tab (the prompts and emails save in their own editor) -->
      <div v-if="areaDirty(a)" class="anna-savebar">
        <oxd-text tag="p" class="anna-unsaved"
          >Unsaved changes on this tab</oxd-text
        >
        <oxd-button
          display-type="ghost"
          label="Undo my changes"
          :disabled="busy"
          @click="undoArea(a)"
        />
        <oxd-button
          display-type="secondary"
          label="Save changes"
          :disabled="busy || !!offline"
          @click="saveArea(a)"
        />
      </div>
      <template v-for="item in itemsOf(a)" :key="item.id">
        <div
          v-if="item.type === 'questions'"
          class="orangehrm-card-container anna-group"
        >
          <oxd-text tag="h6" class="orangehrm-main-title">
            The questions Anna asks
          </oxd-text>
          <oxd-text tag="p" class="anna-purpose">
            In this order: 1. General → 2. The resume → 3. Personality → 4. The
            role → Practical questions → Ending. One question per line, asked
            word for word. Before a call, HR can change one candidate's
            questions on the candidate page ("Anna will ask").
          </oxd-text>
        </div>

        <div
          v-else-if="item.type === 'emailsHelp'"
          class="orangehrm-card-container anna-group"
        >
          <oxd-text tag="h6" class="orangehrm-main-title">
            The emails, in the order a candidate gets them
          </oxd-text>
          <oxd-text tag="p" class="anna-purpose">
            Words in {curly brackets} are filled in for each candidate: keep
            them as they are ({fullName}, {position}, {company}, {companyLegal},
            {assistantName}, {platform}, {jobLink}, {minutes}, {questions},
            {booking}). **bold**, _italic_ and [text](link) work.
          </oxd-text>
        </div>

        <letter-templates v-else-if="item.type === 'letters'" />

        <oxd-text
          v-else-if="item.type === 'heading'"
          tag="h6"
          class="anna-section-head"
        >
          {{ item.text }}
        </oxd-text>

        <prompt-library
          v-else-if="item.type === 'prompt' && !offline"
          :keys="[item.key]"
          collapsible
        />

        <div
          v-else-if="item.type === 'transfer' && !offline"
          class="orangehrm-card-container anna-group"
        >
          <oxd-text tag="h6" class="orangehrm-main-title">
            Copy to another Orbit
          </oxd-text>
          <oxd-text tag="p" class="anna-purpose">
            Copies the instructions, emails, question lists, tones of voice,
            names, letter templates and each vacancy's instructions for Anna to
            another Orbit (for example dev → prod; vacancies go to the ones
            named the same there, letters replace the ones named the same or are
            added). Keys, connections and switches such as test mode and the
            mailbox are never in the file.
          </oxd-text>
          <div class="anna-transfer">
            <oxd-button
              display-type="ghost"
              icon-name="download"
              label="Export settings"
              :disabled="transferring"
              @click="exportSettings"
            />
            <oxd-button
              display-type="ghost"
              icon-name="upload"
              label="Import settings…"
              :disabled="transferring"
              @click="$refs.importFile[0].click()"
            />
            <input
              ref="importFile"
              type="file"
              accept="application/json,.json"
              class="anna-transfer-file"
              @change="importSettings"
            />
          </div>
        </div>

        <div
          v-else-if="item.type === 'quality'"
          class="orangehrm-card-container anna-group"
        >
          <div class="anna-group-head">
            <oxd-text tag="h6" class="orangehrm-main-title">
              Meeting quality
            </oxd-text>
            <oxd-button
              display-type="ghost"
              icon-name="download"
              label="Download logs.md"
              :disabled="qualityLoading"
              @click="downloadQuality"
            />
          </div>
          <oxd-text tag="p" class="anna-purpose">
            How each interview call went technically, a section a day: how long
            Anna took to reply, where the time went at Vapi, and whether the
            meeting bot's audio kept up. No words, no names.
          </oxd-text>
        </div>

        <div
          v-else-if="item.type === 'errors'"
          class="orangehrm-card-container anna-group"
        >
          <div class="anna-group-head">
            <oxd-text tag="h6" class="orangehrm-main-title">Error log</oxd-text>
            <div class="anna-transfer">
              <oxd-button
                display-type="ghost"
                label="Copy all"
                :disabled="!errorText"
                @click="copyErrors"
              />
              <oxd-button
                display-type="ghost"
                label="Refresh"
                :disabled="errorsLoading"
                @click="loadErrors"
              />
              <oxd-button
                display-type="ghost"
                label="Clear"
                :disabled="!errorText || errorsLoading"
                @click="clearErrors"
              />
            </div>
          </div>
          <oxd-text tag="p" class="anna-purpose">
            The errors Orbit got from or about ANNA, newest first: a failed
            call, a meeting Anna won't join, an invitation not sent. Copy them
            here after the message on screen is gone.
          </oxd-text>
          <textarea
            class="anna-error-log"
            readonly
            rows="12"
            :value="errorText || 'No errors.'"
            @focus="$event.target.select()"
          ></textarea>
        </div>

        <div
          v-else-if="item.type === 'tools'"
          class="orangehrm-card-container anna-group"
        >
          <div class="anna-group-head">
            <oxd-text tag="h6" class="orangehrm-main-title">Tools</oxd-text>
            <oxd-button
              display-type="text"
              :icon-name="open.tools ? 'chevron-up' : 'chevron-down'"
              :label="open.tools ? 'Hide' : 'Show'"
              @click="open.tools = !open.tools"
            />
          </div>
          <oxd-text tag="p" class="anna-purpose">
            Copy to another Orbit, the error log and meeting quality: for when
            something needs looking into.
          </oxd-text>
        </div>

        <div
          v-else-if="item.type === 'later'"
          class="orangehrm-card-container anna-group"
        >
          <div class="anna-group-head">
            <oxd-text tag="h6" class="orangehrm-main-title">
              Not in use yet
            </oxd-text>
            <oxd-button
              display-type="text"
              :icon-name="open.later ? 'chevron-up' : 'chevron-down'"
              :label="open.later ? 'Hide' : 'Show'"
              @click="open.later = !open.later"
            />
          </div>
          <oxd-text tag="p" class="anna-purpose">
            Set up for later; nothing here runs yet:
            {{ laterTitles }}.
          </oxd-text>
        </div>

        <div
          v-else-if="item.type === 'card'"
          v-show="!item.later || open.later"
          :id="`anna-card-${item.id}`"
          class="orangehrm-card-container anna-group"
        >
          <div class="anna-group-head">
            <oxd-text tag="h6" class="orangehrm-main-title">
              {{ item.title }}
            </oxd-text>
            <span v-if="item.collapsible" class="anna-count">
              {{ countOf(item) }}
            </span>
            <span
              v-if="item.part === 'admin' && !item.group.configured"
              class="anna-chip anna-chip--missing"
            >
              Needs setup
            </span>
            <oxd-button
              v-if="item.collapsible"
              display-type="text"
              :icon-name="isOpen(item) ? 'chevron-up' : 'chevron-down'"
              :label="isOpen(item) ? 'Hide' : 'Edit'"
              :disabled="cardDirty(item)"
              @click="open[item.id] = !open[item.id]"
            />
          </div>
          <oxd-text v-if="item.purpose" tag="p" class="anna-purpose">
            {{ item.purpose }}
          </oxd-text>
          <div v-show="isOpen(item)">
            <oxd-divider v-if="item.fields.length" />
            <oxd-grid
              v-if="item.fields.length"
              :cols="2"
              class="orangehrm-full-width-grid"
            >
              <oxd-grid-item
                v-for="field in item.fields"
                :key="field.key"
                :class="{
                  'anna-wide': ['textarea', 'list'].includes(field.type),
                }"
              >
                <div v-if="field.type === 'bool'" class="anna-switch">
                  <oxd-text tag="p" class="anna-switch-label">
                    {{ field.label }}
                  </oxd-text>
                  <oxd-switch-input v-model="form[field.key]" />
                </div>
                <!-- a time zone: picked (or searched) from ANNA's list, each with its offset now -->
                <search-select
                  v-else-if="field.type === 'zone'"
                  v-model="form[field.key]"
                  :label="field.label"
                  :options="
                    (field.options || []).map((o) => ({
                      id: o.value,
                      label: o.label,
                    }))
                  "
                />
                <div v-else-if="field.type === 'list'" class="anna-list">
                  <oxd-text tag="p" class="anna-switch-label">
                    {{ field.label }}
                  </oxd-text>
                  <div
                    v-for="(_, i) in form[field.key]"
                    :key="i"
                    class="anna-list-row"
                  >
                    <span class="anna-list-num">{{ i + 1 }}</span>
                    <oxd-input-field
                      v-model="form[field.key][i]"
                      :placeholder="
                        field.key === 'personalities'
                          ? 'Name: how she speaks'
                          : 'Type a question'
                      "
                    />
                    <oxd-icon-button
                      name="chevron-up"
                      title="Move up"
                      :disabled="i === 0"
                      @click="moveItem(field.key, i, -1)"
                    />
                    <oxd-icon-button
                      name="chevron-down"
                      title="Move down"
                      :disabled="i === form[field.key].length - 1"
                      @click="moveItem(field.key, i, 1)"
                    />
                    <oxd-icon-button
                      name="trash"
                      title="Remove"
                      @click="removeItem(field.key, i)"
                    />
                  </div>
                  <oxd-button
                    display-type="text"
                    icon-name="plus"
                    :label="
                      {
                        personalities: 'Add personality',
                      }[field.key] || 'Add question'
                    "
                    @click="addItem(field.key)"
                  />
                </div>
                <!-- the interview days: ticked, kept as ANNA stores them ("Mon,Tue,...") -->
                <div v-else-if="field.key === 'booking_days'" class="anna-list">
                  <oxd-text tag="p" class="anna-switch-label">
                    {{ field.label }}
                  </oxd-text>
                  <div class="anna-days">
                    <label v-for="d in WEEKDAYS" :key="d" class="anna-day">
                      <input
                        type="checkbox"
                        :checked="dayOn(form[field.key], d)"
                        @change="toggleDay(field.key, d, $event.target.checked)"
                      />
                      {{ d }}
                    </label>
                  </div>
                </div>
                <!-- a time of day: the browser's own picker, kept as hh:mm -->
                <div
                  v-else-if="TIME_FIELDS.includes(field.key)"
                  class="anna-list"
                >
                  <oxd-text tag="p" class="anna-switch-label">
                    {{ field.label }}
                  </oxd-text>
                  <input
                    v-model="form[field.key]"
                    type="time"
                    step="1800"
                    class="oxd-input oxd-input--active anna-time"
                  />
                </div>
                <!-- the default personality: one of the list above, not typed -->
                <oxd-input-field
                  v-else-if="field.key === 'default_personality'"
                  :model-value="personalityOption(form[field.key])"
                  type="select"
                  :label="field.label"
                  :options="personalityOptions"
                  @update:model-value="
                    form[field.key] = $event ? $event.id : ''
                  "
                />
                <oxd-input-field
                  v-else
                  v-model="form[field.key]"
                  :label="field.label"
                  :type="inputType(field)"
                  :placeholder="placeholder(field)"
                  autocomplete="off"
                />
                <oxd-text v-if="field.help" tag="p" class="anna-help">
                  {{ field.help }}
                </oxd-text>
              </oxd-grid-item>
            </oxd-grid>

            <!-- Admin: the Vapi assistants (pick one, edit its model and voice) -->
            <template v-if="item.part === 'admin' && !offline">
              <assistant-picker
                v-if="PICKER_OF[item.group.key]"
                :only="PICKER_OF[item.group.key]"
                :current="savedAssistantIds"
                :busy="busy"
                @pick="onPickAssistant"
              />
              <template v-if="PERSONA_OF[item.group.key]">
                <div class="anna-keys-toggle">
                  <oxd-button
                    display-type="text"
                    :icon-name="
                      openTech[item.group.key] ? 'chevron-up' : 'chevron-down'
                    "
                    :label="`${
                      openTech[item.group.key] ? 'Hide' : 'Show'
                    } the Vapi assistant's model and voice`"
                    @click="toggleTech(item.group.key)"
                  />
                </div>
                <template v-if="seenTech[item.group.key]">
                  <persona-editor
                    v-for="slot in [].concat(PERSONA_OF[item.group.key])"
                    v-show="openTech[item.group.key]"
                    :key="`${slot}-${savedAssistantIds[slot] || ''}`"
                    :persona-slot="slot"
                    :chat="!PICKER_OF[item.group.key]"
                  />
                </template>
              </template>
            </template>
            <template v-if="item.group.key === 'mailbox' && item.part === 'hr'">
              <oxd-text tag="p" class="anna-help">
                The mailbox import is
                {{ savedValue('mailbox_on') ? 'on' : 'off' }} (Admin → Jobs
                mailbox).
              </oxd-text>
              <mailbox-log v-if="!offline" compact />
            </template>

            <oxd-text v-if="cardDirty(item)" tag="p" class="anna-unsaved">
              Not saved yet
            </oxd-text>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import {OxdSwitchInput} from '@ohrm/oxd';
import PersonaEditor from '../components/PersonaEditor.vue';
import AssistantPicker from '../components/AssistantPicker.vue';
import MailboxLog from '../components/MailboxLog.vue';
import SearchSelect from '../components/SearchSelect.vue';
import PromptLibrary from '../components/PromptLibrary.vue';
import LetterTemplates from '../components/LetterTemplates.vue';
import {guardLeaving} from '../util/leaveGuard';
import {copyText} from '../util/review';

const ADMIN = 'Admin';

/*
 * Where things go. HR's areas list their blocks in order: a prompt (ANNA's Prompts::LIBRARY
 * key), one of ANNA's setting groups (only the fields HR may change: hrKeys from the API),
 * or a fixed block. Everything else of a group (keys, ids, addresses, timings) is in Admin,
 * which only Orbit Admins see. A group ANNA adds later lands at the end of Interview / Admin.
 */
const LAYOUT = {
  Applications: ['mailbox', 'screening'],
  Emails: [
    'outreach',
    'emailsHelp',
    'prompt:email_first',
    'initial',
    'prompt:email_missing',
    'prompt:email_reject',
    'prompt:email_booking',
    'prompt:email_invite',
    'prompt:email_resume',
  ],
  Letters: ['letters'],
  Booking: ['booking'],
  Interview: [
    'general',
    'questions',
    'block_opening',
    'block_cv',
    'block_deep_dive',
    'block_role',
    'block_screening',
    'block_ending',
    'personalities',
  ],
};
// Admin: each of Anna's jobs in one place: its Vapi assistant (card), then the instructions it follows
const JOBS = [
  {group: 'cv', prompts: ['cv_screening']},
  {group: 'initial_reply', prompts: ['reply_reader', 'email_reader']},
  {group: 'hr_voice', prompts: ['interview']},
  {group: 'review', prompts: ['interview_review']},
];
// groups with no fields of their own that are still shown: a pointer, the mailbox's log
const HR_EMPTY = ['block_role', 'mailbox'];
// set up for later, nothing runs yet: folded at the end of Admin
const LATER = ['tech'];
// the question blocks open on "Edit", so the outline fits on a screen
const COLLAPSIBLE = (key) => key.startsWith('block_');

// Admin: which Vapi assistant a group's card picks and edits
const PICKER_OF = {hr_voice: 'general', tech: 'technical'};
const PERSONA_OF = {
  hr_voice: 'general',
  tech: 'technical',
  cv: 'cv',
  // replies to the questions, and candidates' other emails
  initial_reply: ['reply', 'mail'],
  review: 'review',
};
// a group with an HR card too gets its own name for the Admin card
const ADMIN_TITLES = {
  cv: '1 · Reads CVs: Vapi assistant',
  initial_reply: '2 · Reads replies and other emails: Vapi assistants',
  hr_voice: "3 · Runs the interview: Anna's voice and model (Vapi)",
  review: '4 · Scores the interview: Vapi assistant',
  meetings: 'Meetings: company Jitsi and time zone',
  meetbot: 'Anna joining meetings: timings',
  mailbox: 'Jobs mailbox: connection',
  booking: "Orbit's address (links in emails)",
};
// the assistant ids are chosen with the picker, not typed
const PICKED_FIELDS = ['vapi_assistant_general', 'vapi_assistant_technical'];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// hh:mm settings, picked rather than typed
const TIME_FIELDS = ['booking_from', 'booking_to'];

const AREA_KEY = 'orbit.annaSettings.area';
const readArea = () => {
  try {
    return localStorage.getItem(AREA_KEY) || '';
  } catch (e) {
    return '';
  }
};
const copy = (value) => JSON.parse(JSON.stringify(value ?? null));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default {
  name: 'AnnaSettings',
  components: {
    'oxd-switch-input': OxdSwitchInput,
    'persona-editor': PersonaEditor,
    'assistant-picker': AssistantPicker,
    'mailbox-log': MailboxLog,
    'prompt-library': PromptLibrary,
    'letter-templates': LetterTemplates,
    'search-select': SearchSelect,
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/settings',
    );
    return {http, ADMIN, PICKER_OF, PERSONA_OF, WEEKDAYS, TIME_FIELDS};
  },
  data() {
    return {
      busy: false,
      loaded: false,
      encryptionEnabled: true,
      hrKeys: [],
      // ANNA is away: the last copy of her settings, read-only
      offline: null,
      groups: [],
      form: {},
      // the values as loaded or saved, to tell unsaved edits
      saved: {},
      // card id => open (question blocks, "Not in use yet")
      open: {},
      // the Vapi model/voice editors: opened once stays loaded, keeping its edits
      openTech: {},
      seenTech: {},
      transferring: false,
      errors: [],
      errorsLoading: false,
      qualityLoading: false,
      area: readArea(),
    };
  },
  computed: {
    // the personalities as typed above ("Name: how she speaks"), by name
    personalityOptions() {
      return (this.form.personalities || [])
        .map((p) => String(p).split(':')[0].trim())
        .filter(Boolean)
        .map((name) => ({id: name, label: name}));
    },
    areas() {
      // only Orbit Admins open ANNA Settings (apiv2_anna_settings): the Admin tab is always theirs
      return [...Object.keys(LAYOUT), ADMIN];
    },
    // HR's cards and the Admin cards, from ANNA's groups
    cards() {
      const cards = [];
      this.groups.forEach((group) => {
        const fields = group.fields.filter(
          (f) => !PICKED_FIELDS.includes(f.key),
        );
        const hr = fields.filter((f) => this.hrKeys.includes(f.key));
        const admin = fields.filter((f) => !this.hrKeys.includes(f.key));
        const later = LATER.includes(group.key);
        if (!later && (hr.length || HR_EMPTY.includes(group.key))) {
          cards.push({
            type: 'card',
            id: `${group.key}-hr`,
            part: 'hr',
            group,
            fields: hr,
            title: group.title,
            purpose: group.purpose,
            collapsible: COLLAPSIBLE(group.key) && hr.length > 0,
          });
        }
        if (admin.length || PERSONA_OF[group.key] || later) {
          cards.push({
            type: 'card',
            id: `${group.key}-admin`,
            part: 'admin',
            group,
            later,
            fields: admin,
            title: ADMIN_TITLES[group.key] || group.title,
            // a group that also has an HR card explains itself there
            purpose: hr.length ? '' : group.purpose,
            collapsible: false,
          });
        }
      });
      return cards;
    },
    laterTitles() {
      return this.groups
        .filter((g) => LATER.includes(g.key))
        .map((g) => g.title)
        .join(', ');
    },
    adminNeedsSetup() {
      return this.cards.some(
        (c) => c.part === 'admin' && !c.later && !c.group.configured,
      );
    },
    testMode() {
      return !!this.savedValue('outreach_test_mode');
    },
    testAddress() {
      return this.savedValue('outreach_test_address');
    },
    // the saved ids (not unsaved edits): what Vapi calls actually use
    savedAssistantIds() {
      return {
        general: this.savedValue('vapi_assistant_general') || '',
        technical: this.savedValue('vapi_assistant_technical') || '',
      };
    },
    // one line per error: when | who | where | what
    errorText() {
      return this.errors
        .map((e) =>
          [e.at, e.user || 'background job', e.source, e.message].join(' | '),
        )
        .join('\n');
    },
    anyDirty() {
      return Object.keys(this.form).some(
        (key) => !same(this.form[key], this.saved[key]),
      );
    },
  },
  watch: {
    area(area) {
      if (area === ADMIN) this.loadErrors();
      try {
        localStorage.setItem(AREA_KEY, area);
      } catch (e) {
        // private mode or blocked storage: the area just isn't remembered
      }
    },
    areas(areas) {
      if (!areas.includes(this.area)) this.area = areas[0];
    },
  },
  created() {
    if (!this.areas.includes(this.area)) this.area = this.areas[0];
    this.load();
  },
  mounted() {
    this.unguard = guardLeaving(() => this.anyDirty);
  },
  beforeUnmount() {
    this.unguard();
  },
  methods: {
    // an area's blocks, in order; Admin gets every admin card, "Not in use yet" last
    itemsOf(area) {
      if (area === ADMIN) {
        const admin = this.cards.filter((c) => c.part === 'admin');
        const now = admin.filter((c) => !c.later);
        const later = admin.filter((c) => c.later);
        const jobGroups = JOBS.map((j) => j.group);
        return [
          {
            type: 'heading',
            id: 'h-jobs',
            text: "Anna's jobs: the assistant and its instructions",
          },
          ...JOBS.flatMap((job) => [
            ...now.filter((c) => c.group.key === job.group),
            ...job.prompts.map((key) => ({
              type: 'prompt',
              id: `prompt:${key}`,
              key,
            })),
          ]),
          {
            type: 'heading',
            id: 'h-connections',
            text: 'Connections and timings',
          },
          ...now.filter((c) => !jobGroups.includes(c.group.key)),
          ...(later.length ? [{type: 'later', id: 'later'}] : []),
          ...later,
          // copy, error log, meeting quality: for when something needs looking into
          {type: 'tools', id: 'tools'},
          ...(this.open.tools
            ? [
                {type: 'transfer', id: 'transfer'},
                {type: 'errors', id: 'errors'},
                {type: 'quality', id: 'quality'},
              ]
            : []),
        ];
      }
      const hr = this.cards.filter((c) => c.part === 'hr');
      const placed = Object.values(LAYOUT).flat();
      const items = LAYOUT[area].flatMap((entry) => {
        if (['questions', 'emailsHelp', 'letters'].includes(entry)) {
          return [{type: entry, id: entry}];
        }
        if (entry.startsWith('prompt:')) {
          const key = entry.slice(7);
          return [{type: 'prompt', id: entry, key}];
        }
        return hr.filter((c) => c.group.key === entry);
      });
      // a group ANNA adds later, until it gets its place above
      if (area === 'Interview') {
        items.push(...hr.filter((c) => !placed.includes(c.group.key)));
      }
      return items;
    },
    isOpen(card) {
      return !card.collapsible || !!this.open[card.id] || this.cardDirty(card);
    },
    countOf(card) {
      const lists = card.fields.filter((f) => f.type === 'list');
      if (!lists.length) {
        const n = card.fields.length;
        return `${n} template${n === 1 ? '' : 's'}`;
      }
      const n = lists.reduce(
        (sum, f) => sum + (this.form[f.key] || []).length,
        0,
      );
      return `${n} question${n === 1 ? '' : 's'}`;
    },
    cardDirty(card) {
      return card.fields.some(
        (f) => !same(this.form[f.key], this.saved[f.key]),
      );
    },
    savedValue(key) {
      for (const group of this.groups) {
        const field = group.fields.find((f) => f.key === key);
        if (field) return field.value;
      }
      return null;
    },
    goToCard(id) {
      this.area = this.areas.find((a) =>
        this.itemsOf(a).some((i) => i.id === id),
      );
      this.$nextTick(() =>
        document
          .getElementById(`anna-card-${id}`)
          ?.scrollIntoView({behavior: 'smooth', block: 'start'}),
      );
    },
    toggleTech(key) {
      this.seenTech[key] = true;
      this.openTech[key] = !this.openTech[key];
    },
    // ANNA's answer; `keep` are keys whose unsaved edits stay on the page
    apply(data, keep = []) {
      const kept = Object.fromEntries(keep.map((k) => [k, this.form[k]]));
      this.offline = data.offline || null;
      this.encryptionEnabled = data.encryptionEnabled ?? true;
      if (this.area === ADMIN && !this.errors.length) this.loadErrors();
      if (data.hrKeys) this.hrKeys = data.hrKeys;
      this.groups = data.groups;
      const form = {};
      data.groups.forEach((group) =>
        group.fields.forEach((field) => {
          if (field.type === 'secret') {
            form[field.key] = '';
          } else if (field.type === 'bool') {
            form[field.key] = !!field.value;
          } else if (field.type === 'list') {
            form[field.key] = [...(field.value || [])];
          } else {
            form[field.key] = field.value ?? '';
          }
        }),
      );
      this.saved = copy(form);
      this.form = {...form, ...kept};
      this.loaded = true;
    },
    load() {
      this.busy = true;
      this.http
        .request({method: 'GET'})
        .then((response) => this.apply(response.data.data))
        .finally(() => {
          this.busy = false;
        });
    },
    // the setting fields on a tab (cards with fields; prompts aren't these)
    areaKeys(area) {
      return this.itemsOf(area).flatMap((i) =>
        (i.fields || []).map((f) => f.key),
      );
    },
    areaDirty(area) {
      return this.areaKeys(area).some(
        (k) => !same(this.form[k], this.saved[k]),
      );
    },
    undoArea(area) {
      this.areaKeys(area).forEach((k) => {
        this.form[k] = copy(this.saved[k]);
      });
    },
    // the switches that reach real people: confirm before they do
    confirmGoingLive(keys) {
      const changed = (key) =>
        keys.includes(key) && !same(this.form[key], this.saved[key]);
      if (
        changed('outreach_test_mode') &&
        !this.form.outreach_test_mode &&
        !window.confirm(
          'Turn test mode off? From now on candidate emails (interview invitations and more) go to the real candidates.',
        )
      ) {
        return false;
      }
      if (
        changed('bot_auto_join') &&
        this.form.bot_auto_join &&
        !window.confirm(
          'Let Anna join booked meetings? She will join every meeting booked from now on and interview the candidate.',
        )
      ) {
        return false;
      }
      return true;
    },
    // saves what changed on this tab; other tabs keep their unsaved edits
    saveArea(area) {
      if (this.offline) return;
      const keys = this.areaKeys(area).filter(
        (k) => !same(this.form[k], this.saved[k]),
      );
      if (!keys.length || !this.confirmGoingLive(keys)) return;
      const settings = Object.fromEntries(keys.map((k) => [k, this.form[k]]));
      const keep = Object.keys(this.form).filter(
        (k) => !keys.includes(k) && !same(this.form[k], this.saved[k]),
      );
      this.busy = true;
      this.http
        .request({method: 'PUT', data: {settings}})
        .then((response) => {
          this.apply(response.data.data, keep);
          return this.$toast.saveSuccess();
        })
        // a rejected value is shown by the API client's error toast, naming the field
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
    onPickAssistant(setting, id) {
      if (
        !window.confirm(
          'Use this assistant from now on? Every call it runs (browser tests and meetings) switches to it straight away.',
        )
      ) {
        return;
      }
      const keep = Object.keys(this.form).filter(
        (k) => !same(this.form[k], this.saved[k]),
      );
      this.busy = true;
      this.http
        .request({method: 'PUT', data: {settings: {[setting]: id}}})
        .then((response) => {
          this.apply(response.data.data, keep);
          return this.$toast.saveSuccess();
        })
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
    downloadQuality() {
      this.qualityLoading = true;
      new APIService(window.appGlobal.baseUrl, '/api/v2/anna/quality')
        .request({method: 'GET'})
        .then(({data: {data}}) => {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(
            new Blob([data.markdown], {type: 'text/markdown'}),
          );
          link.download = 'logs.md';
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .finally(() => {
          this.qualityLoading = false;
        });
    },
    loadErrors() {
      this.errorsLoading = true;
      return new APIService(window.appGlobal.baseUrl, '/api/v2/anna/error-log')
        .request({method: 'GET'})
        .then(({data: {data}}) => {
          this.errors = data.errors || [];
        })
        .catch(() => null)
        .finally(() => {
          this.errorsLoading = false;
        });
    },
    clearErrors() {
      if (!window.confirm('Clear the whole error log? This cannot be undone.'))
        return;
      this.errorsLoading = true;
      new APIService(window.appGlobal.baseUrl, '/api/v2/anna/error-log')
        .request({method: 'PUT', data: {}})
        .then(() => {
          this.errors = [];
        })
        .finally(() => {
          this.errorsLoading = false;
        });
    },
    copyErrors() {
      copyText(
        this.$toast,
        this.errorText,
        `${this.errors.length} error(s) copied.`,
      );
    },
    exportSettings() {
      this.transferring = true;
      new APIService(window.appGlobal.baseUrl, '/api/v2/anna/transfer')
        .request({method: 'GET'})
        .then(({data: {data}}) => {
          const blob = new Blob([JSON.stringify(data, null, 2)], {
            type: 'application/json',
          });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `anna-settings-${new Date()
            .toISOString()
            .slice(0, 10)}.json`;
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch(() => null)
        .finally(() => {
          this.transferring = false;
        });
    },
    importSettings(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      file.text().then((text) => {
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          return this.$toast.warn({
            title: 'Not imported',
            message: 'That file is not an ANNA settings export.',
          });
        }
        const when = (data.exportedAt || '').slice(0, 10);
        if (
          !window.confirm(
            `Import the ANNA settings${when ? ` exported ${when}` : ''}?\n\n` +
              'It replaces the instructions and emails (the current ones stay under "Earlier versions"), ' +
              'the question lists, personalities and names on this Orbit, and the instructions for Anna ' +
              'of each vacancy named the same here (no vacancy is created). ' +
              'Letter templates named the same get a new version, the others are added. ' +
              'Keys, connections, test mode and the mailbox are not changed.',
          )
        ) {
          return;
        }
        this.transferring = true;
        return new APIService(window.appGlobal.baseUrl, '/api/v2/anna/transfer')
          .request({method: 'PUT', data: {data}})
          .then(({data: {data: done}}) => {
            const missing = done.missingVacancies || [];
            this.$toast.success({
              title: 'Imported',
              message:
                `${done.prompts.length} prompt(s) updated, ${done.settings.length} setting(s), ` +
                `${(done.vacancies || []).length} vacancy(ies), ` +
                `${(done.letterTemplates || []).length} letter(s).`,
            });
            // no vacancy is made: say which ones the file had that this Orbit hasn't
            if (missing.length) {
              window.alert(
                `No vacancy here is named:\n\n${missing.join('\n')}\n\n` +
                  'Their instructions for Anna were not imported. Create the vacancy and import again.',
              );
            }
            setTimeout(() => window.location.reload(), 1200);
          })
          .catch(() => null)
          .finally(() => {
            this.transferring = false;
          });
      });
    },
    addItem(key) {
      this.form[key] = [...this.form[key], ''];
    },
    removeItem(key, index) {
      this.form[key] = this.form[key].filter((_, i) => i !== index);
    },
    moveItem(key, index, step) {
      const items = [...this.form[key]];
      [items[index], items[index + step]] = [items[index + step], items[index]];
      this.form[key] = items;
    },
    dayOn(value, day) {
      return String(value || '')
        .split(',')
        .map((d) => d.trim())
        .includes(day);
    },
    // in week order, whatever order they were ticked in
    toggleDay(key, day, on) {
      this.form[key] = WEEKDAYS.filter((d) =>
        d === day ? on : this.dayOn(this.form[key], d),
      ).join(',');
    },
    personalityOption(name) {
      return this.personalityOptions.find((o) => o.id === name) || null;
    },
    inputType(field) {
      if (field.type === 'secret') return 'password';
      if (field.type === 'textarea') return 'textarea';
      return 'input';
    },
    placeholder(field) {
      if (field.type !== 'secret') return '';
      return field.isSet ? '••••••••  saved, leave blank to keep' : 'Not set';
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-mode {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 13px;
  &--test {
    background: #fff3d6;
    color: #7a4b00;
  }
  &--live {
    background: #e6f4ea;
    color: #1e5e2e;
  }
}
.anna-mode-badge {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 2px 8px;
  border-radius: 999px;
  color: #fff;
  .anna-mode--test & {
    background: #b8860b;
  }
  .anna-mode--live & {
    background: #2e7d32;
  }
}
.anna-count {
  font-size: 12px;
  color: #6a7482;
}
.anna-savebar {
  position: sticky;
  top: 8px;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 16px;
  border-radius: 12px;
  background: #fff8e6;
  border: 1px solid #f3d38b;
  box-shadow: 0 4px 14px rgba(18, 35, 63, 0.12);
  .anna-unsaved {
    margin-right: auto;
  }
}
.anna-unsaved {
  margin-right: auto;
  font-size: 12px;
  font-weight: 700;
  color: #a05a00;
}
.anna-transfer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 4px 0 12px;
}
.anna-error-log {
  width: 100%;
  box-sizing: border-box;
  font-family: monospace;
  font-size: 12px;
  padding: 8px;
  border: 1px solid #d5dbe5;
  border-radius: 8px;
  resize: vertical;
}
.anna-transfer-file {
  display: none;
}
.anna-intro,
.anna-purpose {
  color: #6a7482;
  font-size: 13px;
  line-height: 1.6;
}
.anna-purpose {
  margin-top: 6px;
  // ANNA writes "What it does / When ..." as separate lines
  white-space: pre-line;
}
// the prompts are what these tabs are for: show a good part of them at once
.anna-wide :deep(textarea) {
  // as tall as its text (a one-line text stays small), a long one scrolls
  field-sizing: content;
  min-height: 72px;
  max-height: 50vh;
  font-size: 13px;
  line-height: 1.5;
}
.anna-section-head {
  margin: 24px 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #12233f;
}
.anna-days {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
}
.anna-day {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  cursor: pointer;
}
.anna-time {
  max-width: 160px;
}
.anna-keys-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0;
}
.anna-group-head {
  display: flex;
  align-items: center;
  gap: 12px;
  .orangehrm-main-title {
    flex-grow: 1;
  }
}
.anna-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 18px;
  border-bottom: 1px solid #e7ebf1;
}
.anna-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: #6a7482;
  cursor: pointer;
  &--active {
    color: #1f5fa8;
    border-bottom-color: #1f5fa8;
  }
}
.anna-tab-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 6px;
  border-radius: 50%;
  background: #b8860b;
  vertical-align: middle;
}
.anna-warn {
  margin-top: 12px;
  color: #c0392b;
  font-size: 13px;
  font-weight: 600;
}
.anna-chip {
  font-size: 11px;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.anna-chip--missing {
  background: #fdf1de;
  color: #b8860b;
}
.anna-group {
  margin-top: 20px;
}
.anna-wide {
  grid-column: 1 / -1;
}
.anna-help {
  margin-top: 4px;
  font-size: 12px;
  color: #9aa4b1;
}
.anna-list-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.anna-list-num {
  flex: 0 0 22px;
  font-size: 12px;
  font-weight: 700;
  color: #9aa4b1;
  text-align: right;
}
.anna-list-row :deep(.oxd-input-group) {
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
  // no per-row label: drop the space OXD reserves for one
  .oxd-input-group__label-wrapper {
    display: none;
  }
}
.anna-list .oxd-button {
  margin-top: 10px;
  margin-left: 22px;
}
.anna-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 45px;
}
.anna-switch-label {
  font-size: 12px;
  font-weight: 700;
  color: #6a7482;
}
</style>
